import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useCreateSuite, useUpdateSuite } from '../api/useTestSuites'
import { useProjects } from '@/features/projects/api/useProjects'
import { toast } from 'sonner'
import type { TestSuite } from '@/types/suite.types'

interface SuiteDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  suite?: TestSuite
  mode: 'create' | 'edit'
  /** Pre-select a project when creating from a filtered view */
  defaultProjectId?: number
}

export function SuiteDialog({
  open,
  onOpenChange,
  suite,
  mode,
  defaultProjectId,
}: SuiteDialogProps) {
  const [name, setName] = useState('')
  const [details, setDetails] = useState('')
  const [parentId, setParentId] = useState<string>('')
  const [nodeOrder, setNodeOrder] = useState('')
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateSuite()
  const updateMutation = useUpdateSuite()
  const { data: projects, isLoading: isLoadingProjects } = useProjects()

  // Reset form when dialog opens
  useEffect(() => {
    if (mode === 'edit' && suite && open) {
      setName(suite.name)
      setDetails(suite.details || '')
      setParentId(suite.parent_id.toString())
      setNodeOrder(suite.node_order?.toString() || '')
    } else if (mode === 'create' && open) {
      setName('')
      setDetails('')
      setParentId(defaultProjectId ? defaultProjectId.toString() : '')
      setNodeOrder('')
    }
    setError(null)
  }, [mode, suite, open, defaultProjectId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isLoading) return

    setError(null)

    if (!name.trim()) {
      setError('Suite name is required')
      return
    }

    if (mode === 'create' && !parentId) {
      setError('Parent project is required')
      return
    }

    const order = nodeOrder.trim() ? parseInt(nodeOrder, 10) : undefined

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync({
          name: name.trim(),
          parent_id: parseInt(parentId, 10),
          details: details.trim() || undefined,
          node_order: order,
        })
        toast.success(`Suite "${name.trim()}" created`)
      } else if (suite) {
        await updateMutation.mutateAsync({
          id: suite.id,
          data: {
            name: name.trim(),
            details: details.trim() || undefined,
            node_order: order,
          },
        })
        toast.success(`Suite "${name.trim()}" updated`)
      }
      onOpenChange(false)
    } catch (err: unknown) {
      let errorMessage =
        mode === 'create' ? 'Failed to create suite' : 'Failed to update suite'

      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } }
        if (axiosError.response?.data?.detail) {
          errorMessage = axiosError.response.data.detail
        }
      } else if (err instanceof Error) {
        errorMessage = err.message
      }

      setError(errorMessage)
    }
  }

  const isLoading = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create Test Suite' : 'Edit Test Suite'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Create a new test suite under a project to organize test cases.'
              : 'Update test suite details.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid gap-2">
              <Label htmlFor="suite-name">
                Suite Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="suite-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Login Tests, Payment Flow"
                disabled={isLoading}
                maxLength={255}
                required
              />
            </div>

            {mode === 'create' && (
              <div className="grid gap-2">
                <Label htmlFor="parent-project">
                  Parent Project <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={parentId}
                  onValueChange={setParentId}
                  disabled={isLoading || isLoadingProjects}
                >
                  <SelectTrigger id="parent-project">
                    <SelectValue
                      placeholder={
                        isLoadingProjects
                          ? 'Loading projects...'
                          : 'Select a project'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {projects?.map((project) => (
                      <SelectItem
                        key={project.id}
                        value={project.id.toString()}
                      >
                        {project.prefix ? `[${project.prefix}] ` : ''}
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="suite-details">Details</Label>
              <Textarea
                id="suite-details"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Description of what this suite covers"
                disabled={isLoading}
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="node-order">Display Order</Label>
              <Input
                id="node-order"
                type="number"
                min="0"
                value={nodeOrder}
                onChange={(e) => setNodeOrder(e.target.value)}
                placeholder="e.g., 1 (lower = higher priority)"
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Controls the sort order among sibling suites.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? mode === 'create'
                  ? 'Creating...'
                  : 'Saving...'
                : mode === 'create'
                  ? 'Create Suite'
                  : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
